/**
 * Single source of truth for Nayak's Tutorials business facts.
 *
 * Everything that describes the organisation -- metadata, JSON-LD, footer
 * contact details, llms.txt, the sitemap -- reads from this file. Change a fact
 * here and it propagates everywhere.
 *
 * Written as CommonJS on purpose: webpack imports it happily from React code
 * (`import businessConfig from '../seo/businessConfig'`) and the Node build
 * scripts can `require()` the exact same object. One file, both worlds, no
 * chance of the build scripts and the UI disagreeing about a phone number.
 *
 * RULE: every value here must be verifiable from the codebase or from
 * documentation supplied by the business. Anything unconfirmed belongs in
 * SEO_CONTENT_GAPS.md, not in this file.
 */

const businessConfig = {
  // ---------------------------------------------------------------------
  // Identity
  //
  // `legalName` uses the typographic apostrophe (U+2019) because that is the
  // dominant form in the codebase (176 occurrences vs 77 straight-quote) and
  // is the correct typographic form for display.
  //
  // `asciiName` exists for contexts that cannot safely carry U+2019: mailto
  // subjects, some analytics payloads, and older third-party systems.
  // ---------------------------------------------------------------------
  legalName: "Nayak’s Tutorials",
  asciiName: "Nayak's Tutorials",
  shortName: "Nayak’s",
  slogan: "Way to Excellence",
  foundingYear: "2000",

  // ---------------------------------------------------------------------
  // Contact
  //
  // EMAIL: the codebase uses three addresses with three distinct jobs. They are
  // NOT interchangeable and this is not a conflict to be "resolved" by deleting
  // one -- see SEO_CONTENT_GAPS.md for the open question.
  //
  //   officialEmail  -- displayed publicly in the header, footer and contact
  //                     page (6 occurrences). Structured data MUST use this
  //                     one, because schema has to match visible content.
  //   enquiryEmail   -- the recipient every enquiry form actually submits to
  //                     (48 occurrences). Preserved exactly as-is; changing it
  //                     would silently break lead capture.
  //   careersEmail   -- careers page only.
  // ---------------------------------------------------------------------
  officialEmail: "info@nayakstutorials.com",
  enquiryEmail: "enquiry@nayakstutorials.com",
  careersEmail: "hr@nayakstutorials.com",

  telephone: "+917710010044",
  telephoneDisplay: "+91 77100 10044",

  // ---------------------------------------------------------------------
  // Head office. This is the only address confirmed in the codebase (it is
  // rendered on the contact page and in the footer). Individual centre
  // addresses are NOT known -- see SEO_CONTENT_GAPS.md.
  // ---------------------------------------------------------------------
  address: {
    streetAddress:
      "209, Vardhaman Industrial Complex, Gokul Nagar, Old Agra Road",
    addressLocality: "Thane West",
    addressRegion: "Maharashtra",
    postalCode: "400601",
    addressCountry: "IN",
  },

  // Taken from the Google Maps embed on the contact page.
  geo: { latitude: 19.208120, longitude: 72.975585 },

  // ---------------------------------------------------------------------
  // Scale and accreditation.
  //
  // These are the figures the site already publishes on the homepage counters.
  // They are the business's own claims; treat them as marketing copy supplied
  // by the client rather than independently verified fact.
  // ---------------------------------------------------------------------
  accreditation: "ISO 9001:2015 Certified Institution",
  staffCount: "500+",
  alumniCount: "300,000+",
  allIndiaRankersYears: "20+",
  yearsOfExperience: "25+",

  // Cities with a physical presence, per the centre selector used across the
  // enquiry forms.
  serviceAreas: [
    "Thane",
    "Mulund",
    "Mira Road",
    "Bandra",
    "Kalwa",
    "Ghatkopar",
    "Satara",
  ],

  /**
   * Official profiles.
   *
   * Only URLs that actually appear in the codebase are listed. `sameAs` is an
   * identity claim -- a wrong URL here tells search engines the business is a
   * different entity, so guessing is worse than omitting.
   *
   * NOTE: the codebase contains two YouTube URLs (a handle and a channel ID)
   * that appear to be the same channel. Both are listed because both resolve;
   * confirm and reduce to one. Recorded in SEO_CONTENT_GAPS.md.
   */
  sameAs: [
    "https://www.facebook.com/nayakstutorialss/",
    "https://www.instagram.com/nayakstutorialss/",
    "https://www.youtube.com/@NayaksTutorials13",
  ],

  /**
   * Programmes offered. `slug` links a programme to its canonical route so
   * internal linking and Course markup stay in sync with the router.
   * A null slug means the programme is offered but has no dedicated page yet.
   */
  programmes: [
    { name: "SSC Coaching", slug: "/classes-for-mh-ssc-board", category: "school" },
    { name: "CBSE Coaching", slug: "/cbse-classes", category: "school" },
    { name: "ICSE Coaching", slug: "/icse-classes", category: "school" },
    { name: "IGCSE Coaching", slug: "/igcse-classes", category: "school" },
    { name: "Std VIII, IX and X Coaching", slug: "/8th-9th-10th-cbse-icse-ssc-coaching-classes", category: "school" },
    { name: "Class 10 Score Booster", slug: "/class-10-score-booster-course", category: "school" },
    { name: "XI-XII Science", slug: "/11th-12th-science-coaching-classes-in-thane", category: "college" },
    { name: "XI-XII Commerce", slug: "/11th-12th-commerce-coaching-classes-in-thane", category: "college" },
    { name: "NEET-UG", slug: "/neet-coaching-classes-in-thane", category: "competitive" },
    { name: "IIT-JEE", slug: "/iit-jee-classes-in-thane", category: "competitive" },
    { name: "MHT-CET", slug: "/engineering", category: "competitive" },
    { name: "CA and CS", slug: "/ca", category: "competitive" },
    { name: "Olympiads", slug: "/olympiads", category: "competitive" },
    { name: "NSAT", slug: "/Nsat", category: "other" },
    { name: "TYBCOM", slug: "/tybcom", category: "college" },
    { name: "Pre-school and Day Care", slug: "/preschool", category: "other" },
    { name: "Performance Booster", slug: null, category: "other" },
  ],

  /**
   * Centre names exactly as they appear in the enquiry form selectors.
   *
   * Addresses, phone numbers and opening hours per centre are NOT in the
   * codebase. Until the business supplies them, individual centre pages cannot
   * be built without inventing data -- which would produce exactly the doorway
   * pages Google penalises. Tracked in SEO_CONTENT_GAPS.md.
   */
  centres: [
    "Thane Vardhaman",
    "Thane Naupada",
    "Thane Vartak Nagar",
    "Thane Vasant Vihar",
    "Thane Manpada",
    "Thane Hiranandani",
    "Thane Anand Nagar",
    "Kalwa",
    "Mulund Veena Nagar",
    "Mulund East",
    "Mira Road Sec.5",
    "Mira Road Jangid",
    "Satara",
    "Bandra",
    "EDUACE GROUP TUITIONS (Mulund East)",
    "EDUACE GROUP TUITIONS (Ghatkopar East)",
    "EDUACE GROUP TUITIONS (Mulund West)",
  ],
};

module.exports = businessConfig;
